import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import UAParser from 'ua-parser-js';
import { Label } from '@ets/design-system';

import { useSelector } from '_common/hooks';
import { useListSessionsQuery, useDeleteSessionMutation } from '../../UserSettingsApi';
import { selectAccounts } from 'Auth/redux/localStorageSlice';

import { Table, Cell, type TableColumn, type TableRow } from '_common/components/Table';
import styles from './SessionTable.module.scss';

type RowData = Session & { id: string };

const SessionTable = () => {
  const intl = useIntl();
  const userId = useSelector((state) => state.auth.userId);
  const accounts = useSelector(selectAccounts);

  const { data: sessions, isFetching } = useListSessionsQuery();
  const [deleteSession] = useDeleteSessionMutation();

  const columns = useMemo<TableColumn<RowData>[]>(() => {
    return [
      {
        id: 'platform',
        accessorKey: 'last_user_agent',
        enableSorting: false,
        minWidth: 240,
        header: intl.formatMessage({
          id: 'settings.security.platform',
        }),
        cell: (info) => {
          const session = info.row.original;

          return (
            <Cell testId={`${session.token}-platform-column`}>
              {parsePlatformValue(session.last_user_agent)}
            </Cell>
          );
        },
      },
      {
        id: 'ipAdress',
        accessorKey: 'last_ip_address',
        enableSorting: false,
        size: 200,
        header: intl.formatMessage({
          id: 'settings.security.ipAdress',
        }),
        cell: (info) => {
          const session = info.row.original;

          return (
            <Cell testId={`${session.token}-ip-adress-column`}>{session.last_ip_address}</Cell>
          );
        },
      },
      {
        id: 'expire',
        accessorKey: 'expires',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({
          id: 'EXPIRY_DATE',
        }),
        cell: (info) => {
          const session = info.row.original;

          return (
            <Cell testId={`${session.token}-expire-column`}>
              <Cell.DateTime date={{ date: session.expires }} time={{ time: session.expires }} />
            </Cell>
          );
        },
      },
      {
        id: 'status',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({
          id: 'STATUS',
        }),
        cell: (info) => {
          const session = info.row.original;

          if (session.token !== accounts[userId].token) {
            return null;
          }

          return (
            <Cell testId={`${session.token}-current-column`}>
              <Label size="s" fullWidth variant="green" testId="current">
                <FormattedMessage id="global.current" />
              </Label>
            </Cell>
          );
        },
      },
    ];
  }, []);

  const parsePlatformValue = (platform: string | undefined) => {
    const parser = new UAParser();
    if (platform) {
      parser.setUA(platform);
    }
    const result = parser.getResult();
    const str = intl.formatMessage(
      { id: 'settings.security.on' },
      {
        operatingSystem: result.os.name,
        browser: result.browser.name,
        version: result.browser.version,
      },
    );

    return str;
  };

  const rowsData = useMemo(() => {
    if (!sessions) {
      return [];
    }

    return sessions.map<TableRow<RowData>>((session) => {
      return {
        id: session.token,
        ...session,

        suffix: {
          actions: [
            {
              label: intl.formatMessage({ id: 'global.remove' }),
              onClick: () => {
                deleteSession(session.token);
              },
            },
          ],
        },
      };
    });
  }, [sessions]);

  return (
    <div className={styles.root}>
      <Table
        identity="userPreferences/security_session"
        options={{ columns, data: rowsData }}
        renderFooter={() => null}
        isLoading={isFetching}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        testId="userPreferences_security_session"
        selectable={false}
        virtualized={false}
      />
    </div>
  );
};

export default SessionTable;
